import {
  TERMINOLOGY_CONFIGURATION_CODES,
  TERMINOLOGY_USAGE_KINDS,
  TerminologyUsageDeclaration,
  TerminologyUsageKind,
  TerminologyUsageSurface,
  defineTerminologyUsage,
} from '@app/shared/terminology/terminology-usage';
import {
  TERMINOLOGY_TOKENS,
  TerminologyTokenCode,
} from '@app/shared/terminology/terminology-token.catalog';

type Token = (typeof TERMINOLOGY_TOKENS)[keyof typeof TERMINOLOGY_TOKENS];
type UsageSpec = readonly [
  token: Token,
  fallbackText: string,
  kind: TerminologyUsageKind,
];

const surface = (
  surfaceCode: string,
  componentCode: string,
  ...routes: string[]
): TerminologyUsageSurface => ({
  featureCode: 'phms',
  surfaceCode,
  componentCode,
  locations: routes.map((value) => ({ kind: 'route', value })),
});

const toKebabCase = (value: string): string =>
  value.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();

const usageGroup = <const TSpecs extends Readonly<Record<string, UsageSpec>>>(
  usagePrefix: string,
  usageSurface: TerminologyUsageSurface,
  specs: TSpecs,
) =>
  Object.fromEntries(
    Object.entries(specs).map(([key, [token, fallbackText, kind]]) => [
      key,
      defineTerminologyUsage<TerminologyTokenCode>({
        usageId: `${usagePrefix}.${toKebabCase(key)}`,
        configurationCode: TERMINOLOGY_CONFIGURATION_CODES.programPack,
        token,
        fallbackText,
        kind,
        surface: usageSurface,
      }),
    ]),
  ) as { readonly [TKey in keyof TSpecs]: TerminologyUsageDeclaration };

const templateText = TERMINOLOGY_USAGE_KINDS.templateText;
const componentInput = TERMINOLOGY_USAGE_KINDS.componentInput;
const formLabel = TERMINOLOGY_USAGE_KINDS.formLabel;
const gridHeader = TERMINOLOGY_USAGE_KINDS.gridHeader;
const message = TERMINOLOGY_USAGE_KINDS.message;

const dashboard = surface(
  'parole-dashboard',
  'ParoleDashboardComponent',
  '/dashboard',
  '/phms/dashboard',
);
const eligibility = surface(
  'eligibility-status',
  'PhmsEligibilityStatusComponent',
  '/case-context',
);
const hearingDetailFlyout = surface(
  'hearing-detail-flyout',
  'HearingDetailFlyoutComponent',
  '/phms/hearing-schedule',
  '/phms/hearing-detail',
);
const hearingList = surface(
  'hearing-list',
  'HearingListComponent',
  '/phms/hearing-list',
);
const preHearing = surface(
  'pre-hearing-capture',
  'PreHearingCaptureComponent',
  '/phms/hearing-detail',
);
const sdmf = surface(
  'sdmf-analysis',
  'SdmfAnalysisSectionComponent',
  '/phms/hearing-detail',
);
const hearingOffense = surface(
  'hearing-offense',
  'HearingOffenseSectionComponent',
  '/phms/hearing-detail',
);
const hearingSchedule = surface(
  'hearing-schedule',
  'HearingScheduleComponent',
  '/phms/hearing-schedule',
);
const createInmate = surface(
  'create-inmate',
  'PhmsCreateInmateComponent',
  '/phms/intake/create-inmate',
);
const intakeStaging = surface(
  'intake-staging',
  'PhmsIntakeStagingComponent',
  '/phms/intake/staging',
);
const intakeDetail = surface(
  'intake-submission-detail',
  'PhmsIntakeSubmissionDetailComponent',
  '/phms/intake-submissions',
);
const intakeList = surface(
  'intake-submission-list',
  'PhmsIntakeSubmissionListComponent',
  '/phms/intake-submissions',
);
const ipList = surface('ip-list', 'IncarceratedPersonsDashboardComponent', '/phms/incarcerated-persons');
const landing = surface('landing', 'PhmsLandingComponent', '/phms');
const legalDashboard = surface(
  'legal-review-dashboard',
  'LegalReviewDashboardComponent',
  '/phms/legal-reviews',
  '/phms/dashboard',
);
const myHearings = surface(
  'my-hearings',
  'MyHearingsComponent',
  '/phms/my-hearings',
);
const postHearingTasks = surface(
  'post-hearing-tasks',
  'PostHearingTasksComponent',
  '/phms/post-hearing-tasks',
);
const readiness = surface(
  'readiness-summary',
  'PhmsReadinessSummaryComponent',
  '/phms/hearing-detail',
);
const reports = surface('reports', 'PhmsReportsComponent', '/phms/reports');

export const PHMS_OPERATIONAL_TERMINOLOGY = {
  dashboard: usageGroup('phms.dashboard.operational', dashboard, {
    unreadAlerts: [TERMINOLOGY_TOKENS.unreadAlerts, 'Unread alerts', templateText],
    casesWithAlerts: [
      TERMINOLOGY_TOKENS.casesWithAlerts,
      'Cases with alerts',
      templateText,
    ],
    oneAlert: [TERMINOLOGY_TOKENS.oneAlert, '1 alert', message],
    alerts: [
      TERMINOLOGY_TOKENS.operationalAlertInlinePlural,
      'alerts',
      message,
    ],
    allCaseStates: [
      TERMINOLOGY_TOKENS.allCaseStates,
      'All Case States',
      componentInput,
    ],
    allWorkflowStatuses: [
      TERMINOLOGY_TOKENS.allWorkflowStatuses,
      'All Workflow Statuses',
      componentInput,
    ],
    allQueues: [TERMINOLOGY_TOKENS.allQueues, 'All Queues', componentInput],
    status: [TERMINOLOGY_TOKENS.operationalStatus, 'Status', formLabel],
    caseState: [TERMINOLOGY_TOKENS.operationalCaseState, 'Case State', formLabel],
    workflowStatus: [
      TERMINOLOGY_TOKENS.operationalWorkflowStatus,
      'Workflow Status',
      formLabel,
    ],
    queue: [TERMINOLOGY_TOKENS.operationalQueue, 'Queue', formLabel],
    unclaimedQueue: [
      TERMINOLOGY_TOKENS.unclaimedQueue,
      'Unclaimed queue',
      message,
    ],
  }),
  eligibility: usageGroup('phms.eligibility.operational', eligibility, {
    status: [TERMINOLOGY_TOKENS.operationalStatus, 'Status', templateText],
  }),
  hearingDetailFlyout: usageGroup(
    'phms.hearing-detail-flyout.operational',
    hearingDetailFlyout,
    {
      status: [TERMINOLOGY_TOKENS.operationalStatus, 'Status', templateText],
    },
  ),
  hearingList: usageGroup('phms.hearing-list.operational', hearingList, {
    statusFilter: [TERMINOLOGY_TOKENS.operationalStatus, 'Status', formLabel],
    statusColumn: [TERMINOLOGY_TOKENS.operationalStatus, 'Status', gridHeader],
  }),
  preHearing: usageGroup('phms.pre-hearing.operational', preHearing, {
    citizenshipImmigrationStatus: [
      TERMINOLOGY_TOKENS.citizenshipImmigrationStatus,
      'Citizenship / immigration status',
      formLabel,
    ],
  }),
  sdmf: usageGroup('phms.sdmf.operational', sdmf, {
    status: [TERMINOLOGY_TOKENS.operationalStatus, 'Status', templateText],
  }),
  hearingOffense: usageGroup(
    'phms.hearing-offense.operational',
    hearingOffense,
    {
      status: [TERMINOLOGY_TOKENS.operationalStatus, 'Status', gridHeader],
    },
  ),
  hearingSchedule: usageGroup(
    'phms.hearing-schedule.operational',
    hearingSchedule,
    {
      statusColumnPicker: [
        TERMINOLOGY_TOKENS.operationalStatus,
        'Status',
        componentInput,
      ],
      statusColumn: [TERMINOLOGY_TOKENS.operationalStatus, 'Status', gridHeader],
    },
  ),
  createInmate: usageGroup('phms.create-inmate.operational', createInmate, {
    status: [TERMINOLOGY_TOKENS.operationalStatus, 'Status', gridHeader],
  }),
  intakeStaging: usageGroup('phms.intake-staging.operational', intakeStaging, {
    status: [TERMINOLOGY_TOKENS.operationalStatus, 'Status', gridHeader],
  }),
  intakeDetail: usageGroup('phms.intake-detail.operational', intakeDetail, {
    status: [TERMINOLOGY_TOKENS.operationalStatus, 'Status', templateText],
  }),
  intakeList: usageGroup('phms.intake-list.operational', intakeList, {
    status: [TERMINOLOGY_TOKENS.operationalStatus, 'Status', gridHeader],
  }),
  ipList: usageGroup('phms.ip-list.operational', ipList, {
    lifecycleStatus: [
      TERMINOLOGY_TOKENS.operationalLifecycleStatus,
      'Lifecycle status',
      formLabel,
    ],
    lifecycleStatusColumn: [
      TERMINOLOGY_TOKENS.operationalLifecycleStatusCombined,
      'Lifecycle / Status',
      gridHeader,
    ],
  }),
  landing: usageGroup('phms.landing.operational', landing, {
    queue: [TERMINOLOGY_TOKENS.operationalQueue, 'Queue', templateText],
    placeContinuedCasesOnNextDocket: [
      TERMINOLOGY_TOKENS.placeContinuedCasesOnNextDocket,
      'Place continued cases on next docket',
      templateText,
    ],
    casesClearedForScheduling: [
      TERMINOLOGY_TOKENS.casesClearedForScheduling,
      '5 cases cleared for scheduling',
      message,
    ],
    caseAssignmentNeeded: [
      TERMINOLOGY_TOKENS.caseAssignmentNeeded,
      'Need case assignment',
      message,
    ],
    openQueue: [TERMINOLOGY_TOKENS.openQueue, 'Open queue', templateText],
    completeReadinessChecks: [
      TERMINOLOGY_TOKENS.completeReadinessChecks,
      'Complete readiness checks',
      templateText,
    ],
    queued: [TERMINOLOGY_TOKENS.operationalQueued, 'Queued', templateText],
    unassignedCasesDue: [
      TERMINOLOGY_TOKENS.unassignedCasesDue,
      '8 unassigned cases, 3 due within 48 hours',
      message,
    ],
    unblockReadinessBottlenecks: [
      TERMINOLOGY_TOKENS.unblockReadinessBottlenecks,
      'Unblock readiness bottlenecks',
      templateText,
    ],
  }),
  legalDashboard: usageGroup(
    'phms.legal-review-dashboard.operational',
    legalDashboard,
    {
      reviewQueue: [TERMINOLOGY_TOKENS.reviewQueue, 'Review queue', templateText],
      status: [TERMINOLOGY_TOKENS.operationalStatus, 'Status', formLabel],
      allStatuses: [TERMINOLOGY_TOKENS.allStatuses, 'All statuses', componentInput],
      refreshQueue: [
        TERMINOLOGY_TOKENS.refreshQueue,
        'Refresh the queue to try again.',
        message,
      ],
    },
  ),
  myHearings: usageGroup('phms.my-hearings.operational', myHearings, {
    status: [TERMINOLOGY_TOKENS.operationalStatus, 'Status', gridHeader],
  }),
  postHearingTasks: usageGroup(
    'phms.post-hearing-tasks.operational',
    postHearingTasks,
    {
      statusFilter: [TERMINOLOGY_TOKENS.operationalStatus, 'Status', formLabel],
      statusColumn: [TERMINOLOGY_TOKENS.operationalStatus, 'Status', gridHeader],
      queueAndAssignee: [
        TERMINOLOGY_TOKENS.queueAndAssignee,
        'Queue / Assignee',
        gridHeader,
      ],
    },
  ),
  readiness: usageGroup('phms.readiness.operational', readiness, {
    lastReadinessUpdate: [
      TERMINOLOGY_TOKENS.lastReadinessUpdate,
      'Last readiness update',
      templateText,
    ],
  }),
  reports: usageGroup('phms.reports.operational', reports, {
    allQueues: [TERMINOLOGY_TOKENS.allQueues, 'All Queues', componentInput],
    allQueuesInline: [
      TERMINOLOGY_TOKENS.allQueuesInline,
      'All queues',
      componentInput,
    ],
    queueFilter: [TERMINOLOGY_TOKENS.operationalQueue, 'Queue', formLabel],
    queueUppercase: [
      TERMINOLOGY_TOKENS.operationalQueueUppercase,
      'QUEUE',
      gridHeader,
    ],
    queueExportColumn: [
      TERMINOLOGY_TOKENS.operationalQueue,
      'Queue',
      gridHeader,
    ],
    queueAndOwnership: [
      TERMINOLOGY_TOKENS.queueAndOwnership,
      'Queue And Ownership',
      templateText,
    ],
    noMatchingQueueData: [
      TERMINOLOGY_TOKENS.noMatchingQueueData,
      'No matching queue data.',
      message,
    ],
  }),
} as const;
